import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import AppShell from "@/components/kcc/AppShell";
import Login from "@/pages/Login";
import ExecutiveCommandCentre from "@/pages/ExecutiveCommandCentre";
import Mining360 from "@/pages/Mining360";
import MineIntelligence from "@/pages/MineIntelligence";
import Plant360 from "@/pages/Plant360";
import MetallurgicalAccounting from "@/pages/MetallurgicalAccounting";
import Maintenance360 from "@/pages/Maintenance360";
import RefineryMaintenance from "@/pages/RefineryMaintenance";
import Predictive360 from "@/pages/Predictive360";
import Asset360 from "@/pages/Asset360";
import Safety360 from "@/pages/Safety360";
import Environment360 from "@/pages/Environment360";
import Tailings360 from "@/pages/Tailings360";
import Water360 from "@/pages/Water360";
import Security360 from "@/pages/Security360";
import Cybersecurity360 from "@/pages/Cybersecurity360";
import Finance360 from "@/pages/Finance360";
import Warehouse360 from "@/pages/Warehouse360";
import Procurement360 from "@/pages/Procurement360";
import Contractor360 from "@/pages/Contractor360";
import Knowledge360 from "@/pages/Knowledge360";
import Document360 from "@/pages/Document360";
import DigitalTwin360 from "@/pages/DigitalTwin360";
import BoardPortal from "@/pages/BoardPortal";
import ExecutiveReporting from "@/pages/ExecutiveReporting";
import AIAdvisorPage from "@/pages/AIAdvisorPage";
import Notifications from "@/pages/Notifications";
import { MineTechnicalServices, Laboratory360, Engineering360, Projects360, Logistics360, Community360, ESG360, Energy360, Risk360, Legal360 } from "@/pages/Phase4Workspaces";
import { HR360, ValueRealisation, StrategyCentre, SupplierIntelligence, MineLife, ExecutiveBriefing } from "@/pages/Phase5Workspaces";
import { Scenarios, IntegrationHub } from "@/pages/Phase6Workspaces";
import { CapitalPortfolio, DecisionRegister } from "@/pages/Phase7Workspaces";
import { StorylineEngine, PresentationMode } from "@/pages/Phase8Workspaces";
import { Loader2 } from "lucide-react";

function Splash() {
    return (
        <div className="min-h-screen flex items-center justify-center kcc-hex-bg">
            <div className="text-center">
                <div className="w-14 h-14 rounded-md flex items-center justify-center mx-auto" style={{ background: "linear-gradient(135deg, #b87333, #2d68c4)" }}>
                    <span className="font-display font-bold text-white text-xl">K</span>
                </div>
                <div className="font-display text-2xl mt-4">KCC<span className="text-kcc-copper-light">360</span></div>
                <div className="kcc-label mt-2 flex items-center justify-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Initialising secure session…</div>
            </div>
        </div>
    );
}

function Protected({ children }) {
    const { user } = useAuth();
    if (user === null) return <Splash />;
    if (user === false) return <Navigate to="/login" replace />;
    return children;
}

function Shell({ children }) {
    const { user } = useAuth();
    const [notif, setNotif] = useState(0);
    useEffect(() => { api.get("/notifications").then(({ data }) => setNotif(data.count || 0)).catch(() => {}); }, []);
    return <AppShell user={user} notifications={notif}>{children}</AppShell>;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Protected><Shell><ExecutiveCommandCentre /></Shell></Protected>} />
            <Route path="/mining" element={<Protected><Shell><Mining360 /></Shell></Protected>} />
            <Route path="/mine-intelligence" element={<Protected><Shell><MineIntelligence /></Shell></Protected>} />
            <Route path="/plant" element={<Protected><Shell><Plant360 /></Shell></Protected>} />
            <Route path="/metallurgy" element={<Protected><Shell><MetallurgicalAccounting /></Shell></Protected>} />
            <Route path="/maintenance" element={<Protected><Shell><Maintenance360 /></Shell></Protected>} />
            <Route path="/refinery-maintenance" element={<Protected><Shell><RefineryMaintenance /></Shell></Protected>} />
            <Route path="/predictive" element={<Protected><Shell><Predictive360 /></Shell></Protected>} />
            <Route path="/assets" element={<Protected><Shell><Asset360 /></Shell></Protected>} />
            <Route path="/safety" element={<Protected><Shell><Safety360 /></Shell></Protected>} />
            <Route path="/environment" element={<Protected><Shell><Environment360 /></Shell></Protected>} />
            <Route path="/tailings" element={<Protected><Shell><Tailings360 /></Shell></Protected>} />
            <Route path="/water" element={<Protected><Shell><Water360 /></Shell></Protected>} />
            <Route path="/security" element={<Protected><Shell><Security360 /></Shell></Protected>} />
            <Route path="/cyber" element={<Protected><Shell><Cybersecurity360 /></Shell></Protected>} />
            <Route path="/finance" element={<Protected><Shell><Finance360 /></Shell></Protected>} />
            <Route path="/warehouse" element={<Protected><Shell><Warehouse360 /></Shell></Protected>} />
            <Route path="/procurement" element={<Protected><Shell><Procurement360 /></Shell></Protected>} />
            <Route path="/contractors" element={<Protected><Shell><Contractor360 /></Shell></Protected>} />
            <Route path="/knowledge" element={<Protected><Shell><Knowledge360 /></Shell></Protected>} />
            <Route path="/documents" element={<Protected><Shell><Document360 /></Shell></Protected>} />
            <Route path="/twin" element={<Protected><Shell><DigitalTwin360 /></Shell></Protected>} />
            <Route path="/reports" element={<Protected><Shell><ExecutiveReporting /></Shell></Protected>} />
            <Route path="/board" element={<Protected><Shell><BoardPortal /></Shell></Protected>} />
            <Route path="/ai" element={<Protected><Shell><AIAdvisorPage /></Shell></Protected>} />
            <Route path="/notifications" element={<Protected><Shell><Notifications /></Shell></Protected>} />
            <Route path="/mts" element={<Protected><Shell><MineTechnicalServices /></Shell></Protected>} />
            <Route path="/laboratory" element={<Protected><Shell><Laboratory360 /></Shell></Protected>} />
            <Route path="/engineering" element={<Protected><Shell><Engineering360 /></Shell></Protected>} />
            <Route path="/projects" element={<Protected><Shell><Projects360 /></Shell></Protected>} />
            <Route path="/logistics" element={<Protected><Shell><Logistics360 /></Shell></Protected>} />
            <Route path="/community" element={<Protected><Shell><Community360 /></Shell></Protected>} />
            <Route path="/esg" element={<Protected><Shell><ESG360 /></Shell></Protected>} />
            <Route path="/energy" element={<Protected><Shell><Energy360 /></Shell></Protected>} />
            <Route path="/risk" element={<Protected><Shell><Risk360 /></Shell></Protected>} />
            <Route path="/legal" element={<Protected><Shell><Legal360 /></Shell></Protected>} />
            <Route path="/hr" element={<Protected><Shell><HR360 /></Shell></Protected>} />
            <Route path="/value" element={<Protected><Shell><ValueRealisation /></Shell></Protected>} />
            <Route path="/strategy" element={<Protected><Shell><StrategyCentre /></Shell></Protected>} />
            <Route path="/suppliers" element={<Protected><Shell><SupplierIntelligence /></Shell></Protected>} />
            <Route path="/mine-life" element={<Protected><Shell><MineLife /></Shell></Protected>} />
            <Route path="/briefing" element={<Protected><Shell><ExecutiveBriefing /></Shell></Protected>} />
            <Route path="/scenarios" element={<Protected><Shell><Scenarios /></Shell></Protected>} />
            <Route path="/integrations" element={<Protected><Shell><IntegrationHub /></Shell></Protected>} />
            <Route path="/capital-portfolio" element={<Protected><Shell><CapitalPortfolio /></Shell></Protected>} />
            <Route path="/decisions" element={<Protected><Shell><DecisionRegister /></Shell></Protected>} />
            <Route path="/storylines" element={<Protected><Shell><StorylineEngine /></Shell></Protected>} />
            <Route path="/presentation" element={<Protected><Shell><PresentationMode /></Shell></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <AppRoutes />
                <Toaster />
            </BrowserRouter>
        </AuthProvider>
    );
}
